/**
 * Audio Calibration Module for HEAREN
 * Based on ISO 389-8:2004 (Reference equivalent threshold sound pressure levels)
 * and ISO 8253-1:2010 (Pure-tone audiometry)
 */

// Reference Equivalent Threshold Sound Pressure Levels (RETSPL) in dB SPL
// for supra-aural headphones (ISO 389-8:2004)
// These values represent the sound pressure level at each frequency that 
// corresponds to 0 dB HL (hearing level)
export const RETSPL = {
  125: 45.0,
  250: 25.5,
  500: 11.5,
  1000: 7.0,   // Reference frequency
  2000: 9.0,
  4000: 10.5,
  8000: 13.0
};

// Expected threshold at calibration (normal hearing)
// In clinical setting, 25 dB HL is used as comfortable reference
export const CALIBRATION_REFERENCE_DB_HL = 25;

// Maximum safe output level (to prevent hearing damage)
export const MAX_SAFE_DB_HL = 100;

/**
 * Convert dB HL (Hearing Level) to dB SPL (Sound Pressure Level)
 * @param {number} dbHL - Hearing level in dB HL
 * @param {number} frequency - Frequency in Hz
 * @returns {number} Sound pressure level in dB SPL
 */
export function dbHL_to_dbSPL(dbHL, frequency) {
  const retspl = RETSPL[frequency];
  if (retspl === undefined) {
    return dbHL + 10; // Fallback approximation
  }
  return dbHL + retspl;
}

/**
 * Convert dB SPL to digital amplitude (0.0 - 1.0)
 * Reference: 94 dB SPL = amplitude 1.0 (standard calibration)
 * This assumes the audio system is calibrated so that full scale (1.0) 
 * produces approximately 94 dB SPL at the headphone output
 * 
 * @param {number} dbSPL - Sound pressure level in dB SPL
 * @returns {number} Digital amplitude between 0.0 and 1.0
 */
export function dbSPL_to_amplitude(dbSPL) {
  const refSPL = 94; // dB SPL reference for amplitude = 1.0
  const amplitude = Math.pow(10, (dbSPL - refSPL) / 20);
  return amplitude;
}

/**
 * Main function: Calculate playback volume for audiometric testing
 * @param {number} dbHL - Target hearing level in dB HL
 * @param {number} frequency - Test frequency in Hz
 * @param {number} calibrationOffset - User's calibration offset in dB (from calibration phase)
 * @returns {number} Volume between 0.0 and 1.0, capped at 0.9 for safety
 */
export function calculateTestVolume(dbHL, frequency, calibrationOffset = 0) {
  // Step 1: Convert dB HL to dB SPL using ISO 389-8 reference values
  const dbSPL = dbHL_to_dbSPL(dbHL, frequency);
  
  // Step 2: Convert dB SPL to digital amplitude
  let amplitude = dbSPL_to_amplitude(dbSPL);
  
  // Step 3: Apply user's calibration offset
  // The offset is in dB, so we convert it to amplitude multiplier
  amplitude = amplitude * Math.pow(10, calibrationOffset / 20);
  
  // Step 4: Safety cap at 0.9 to prevent distortion and hearing damage
  const finalVolume = Math.min(amplitude * 0.5, 0.9);
  
  // Log for debugging
  console.log(`🔬 AUDIO CALC: ${dbHL} dB HL @ ${frequency} Hz → ${dbSPL.toFixed(1)} dB SPL → amplitude ${finalVolume.toFixed(6)} (offset: ${calibrationOffset.toFixed(1)} dB)`);
  
  return finalVolume;
}

/**
 * Calculate reliability score of audiometric test
 * @param {Array<number>} leftResults - Left ear thresholds in dB HL
 * @param {Array<number>} rightResults - Right ear thresholds in dB HL
 * @param {Array<number>} frequencies - Frequencies tested
 * @returns {Object} { score: number (0-100), warnings: Array<string>, quality: string }
 */
export function calculateReliabilityScore(leftResults, rightResults, frequencies) {
  let score = 100;
  const warnings = [];
  
  // Criterion 1: Left-Right symmetry (expected in most cases)
  let asymmetryCount = 0;
  for (let i = 0; i < frequencies.length; i++) {
    const diff = Math.abs(leftResults[i] - rightResults[i]);
    if (diff > 20) {
      score -= 15;
      asymmetryCount++;
      warnings.push(`Asymétrie importante à ${frequencies[i]} Hz (${diff} dB)`);
    } else if (diff > 10) {
      score -= 5;
      asymmetryCount++;
    }
  }
  
  // Criterion 2: Audiogram slope regularity
  const leftSlope = checkAudiogramSlope(leftResults, frequencies);
  const rightSlope = checkAudiogramSlope(rightResults, frequencies);
  
  if (leftSlope.irregular) {
    score -= 10;
    warnings.push('Courbe audiométrique gauche irrégulière');
  }
  if (rightSlope.irregular) {
    score -= 10;
    warnings.push('Courbe audiométrique droite irrégulière');
  }
  
  // Criterion 3: Extreme values (suspiciously low or high)
  const extremes = checkExtremeValues(leftResults, rightResults);
  if (extremes.hasExtremes) {
    score -= 10;
    warnings.push('Valeurs extrêmes détectées - vérifier environnement');
  }
  
  // Criterion 4: Overall coherence
  if (asymmetryCount >= 3) {
    score -= 10;
    warnings.push('Trop d\'asymétries - vérifier casque (AirPods recommandés) et positionnement');
  }
  
  // Cap score at 0
  score = Math.max(score, 0);
  
  // Determine quality level
  let quality;
  if (score >= 90) quality = 'excellent';
  else if (score >= 75) quality = 'good';
  else if (score >= 60) quality = 'acceptable';
  else quality = 'poor';
  
  return {
    score,
    warnings,
    quality,
    asymmetryCount,
    leftSlope,
    rightSlope
  };
}

/**
 * Check if audiogram slope is regular (no sudden jumps)
 * @param {Array<number>} results - Threshold values in dB HL
 * @param {Array<number>} frequencies - Frequencies
 * @returns {Object} { irregular: boolean, maxJump: number }
 */
function checkAudiogramSlope(results, frequencies) {
  let maxJump = 0;
  let irregular = false;
  
  for (let i = 1; i < results.length; i++) {
    const jump = Math.abs(results[i] - results[i - 1]);
    if (jump > maxJump) maxJump = jump;
    
    // A jump of more than 30 dB between adjacent frequencies is suspicious
    if (jump > 30) {
      irregular = true;
    }
  }
  
  return { irregular, maxJump };
}

/**
 * Check for extreme threshold values
 * @param {Array<number>} leftResults 
 * @param {Array<number>} rightResults 
 * @returns {Object} { hasExtremes: boolean, extremeValues: Array }
 */
function checkExtremeValues(leftResults, rightResults) {
  const allResults = [...leftResults, ...rightResults];
  const extremeValues = [];
  let hasExtremes = false;
  
  allResults.forEach((value, index) => {
    // Values below -10 dB HL are suspicious (too good)
    if (value < -10) {
      extremeValues.push({ index, value, reason: 'Seuil anormalement bas' });
      hasExtremes = true;
    }
    // Values above 90 dB HL might indicate environmental noise or equipment issue
    if (value > 90) {
      extremeValues.push({ index, value, reason: 'Seuil anormalement élevé' });
      hasExtremes = true;
    }
  });
  
  return { hasExtremes, extremeValues };
}

/**
 * Calculate average hearing threshold across all frequencies
 * @param {Array<number>} leftResults 
 * @param {Array<number>} rightResults 
 * @returns {Object} { left: number, right: number, average: number }
 */
export function calculateAverageThreshold(leftResults, rightResults) {
  const leftAvg = leftResults.reduce((sum, val) => sum + val, 0) / leftResults.length;
  const rightAvg = rightResults.reduce((sum, val) => sum + val, 0) / rightResults.length;
  const overall = (leftAvg + rightAvg) / 2;
  
  return {
    left: Math.round(leftAvg * 10) / 10,
    right: Math.round(rightAvg * 10) / 10,
    average: Math.round(overall * 10) / 10
  };
}

/**
 * Interpret hearing loss level based on WHO classification
 * @param {number} averageThreshold - Average threshold in dB HL
 * @returns {Object} { level: string, description: string }
 */
export function interpretHearingLoss(averageThreshold) {
  if (averageThreshold <= 25) {
    return {
      level: 'normal',
      description: 'Audition normale',
      color: 'green'
    };
  } else if (averageThreshold <= 40) {
    return {
      level: 'mild',
      description: 'Perte auditive légère',
      color: 'yellow'
    };
  } else if (averageThreshold <= 60) {
    return {
      level: 'moderate',
      description: 'Perte auditive modérée',
      color: 'orange'
    };
  } else if (averageThreshold <= 80) {
    return {
      level: 'severe',
      description: 'Perte auditive sévère',
      color: 'red'
    };
  } else {
    return {
      level: 'profound',
      description: 'Perte auditive profonde',
      color: 'darkred'
    };
  }
}
